import styled from 'styled-components/macro';

import { AvatarWrapStyled } from 'components/Avatar/Avatar.styled';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';

type ActionMessageStyledProps = {
  hasThumbnail?: boolean;
  isLoggedInUser?: boolean;
};

type MessageContentStyledProps = {
  isActionMessageContent?: boolean;
  hasThumbnail?: boolean;
  isLoggedInUser?: boolean;
};

/** Inner layout when Messages is inside the ticket messages card (flex column; only list scrolls). */
export const MessagesCardInnerStyled = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

/** Scrollable area for the message list (fills card; avoids card-level scrollbar). */
export const MessagesScrollAreaStyled = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

/** Chat list (round top corners to match card; no max-height so flex controls height). */
export const MessagesWrapStyled = styled.div`
  min-width: 200px;
  width: 100%;
  min-height: 100%;
  overflow-y: visible;
  padding: 0.5rem 0.625rem;
  background-color: ${({ theme }): string => theme.colors.surface};
  border-radius: 12px 12px 0 0;
`;

/** Bottom section of messages card (toggle + form; round bottom corners). */
export const MessagesCardBottomStyled = styled.div`
  flex-shrink: 0;
  padding: 0.5rem 0.625rem 0.625rem;
  background-color: ${({ theme }): string => theme.colors.surface};
  border-radius: 0 0 12px 12px;
`;

export const MessageWrapStyled = styled(FlexContainer)`
  &:not(:first-of-type) {
    margin-top: 1.5rem;
  }

  ${AvatarWrapStyled} {
    min-width: 32px;
  }
`;

export const FlexContainerStyled = styled(FlexContainer)<MessageContentStyledProps>`
  ${({ hasThumbnail, isLoggedInUser }): string =>
    hasThumbnail && isLoggedInUser
      ? `
    margin-left: auto;
  `
      : ''}
`;

export const MessageContentStyled = styled(FlexContainer)<MessageContentStyledProps>`
  flex-direction: column;
  align-items: start;
  margin-left: 0.375rem;

  ${({ isLoggedInUser }): string => (isLoggedInUser ? 'align-items: end;' : '')}
  ${({ isActionMessageContent, theme }): string =>
    isActionMessageContent
      ? `
      width: auto;
      align-items: center;
      padding: 6px 16px;
      background-color: ${theme.colors.secondary.variant3};
      border-radius: ${theme.radius.xl};
      `
      : ''}

${({ hasThumbnail, isLoggedInUser, theme }): string =>
    hasThumbnail && isLoggedInUser
      ? `
      background: ${theme.colors.primary.variant14};
  `
      : ''}
`;

export const MessageStyled = styled(Typography)<{ isText?: boolean; isLoggedInUser?: boolean }>`
  margin-top: 0.25rem;
  word-break: break-word;
  white-space: pre-wrap;
  color: ${({ theme }): string => theme.colors.primary.variant11};

  ${({ isText, isLoggedInUser, theme }): string =>
    isText
      ? `
  width: auto;
  padding: 6px 16px;
  border-radius: ${theme.radius.xl};
  background-color: ${
    isLoggedInUser ? theme.colors.primary.variant14 : theme.colors.primary.variant3
  };
  ${isLoggedInUser ? 'margin-left: 20%;' : 'margin-right: 20%;'}
  `
      : ''}
`;

export const ActionMessageStyled = styled(MessageStyled)<ActionMessageStyledProps>`
  margin: 0;
  ${({ isLoggedInUser, theme, hasThumbnail }): string =>
    hasThumbnail
      ? `
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: auto;
    padding: 6px 16px;
    border-radius: ${theme.radius.xl};
    background-color: ${
      isLoggedInUser ? theme.colors.primary.variant14 : theme.colors.primary.variant3
    };
  `
      : ''}
`;

export const AttachmentNameStyled = styled.span`
  font-size: ${({ theme }): string => theme.fontSize.lg};
  font-weight: ${({ theme }): string => theme.fontWeight.bold.toString()};
`;

/** Matches Assign to Dealer label: white in dark mode, black in light mode. */
export const MessageSenderNameStyled = styled.div`
  display: inline-block;
  white-space: nowrap;
  flex-shrink: 1;
  color: ${({ theme }): string =>
    theme.mode === 'dark' ? theme.colors.white : theme.colors.black};
`;

export const ActionMessageSenderNameStyled = styled(MessageSenderNameStyled)``;

export const ActionMessageHeaderStyled = styled(FlexContainer)`
  width: 100%;
  margin-bottom: 0.25rem;
`;

export const MessageSentDateStyled = styled(Typography)`
  flex-shrink: 0;
  margin-left: 0.375rem;
  padding-top: 0.35rem;
  color: ${({ theme }): string => theme.colors.primary.variant8};
`;

export const ActionMessageSentDateStyled = styled(MessageSentDateStyled)`
  margin: 0;
  padding-top: 0;
`;

export const CountdownStyled = styled(Typography)`
  font-weight: ${({ theme }): string => theme.fontWeight.bold.toString()};
  color: ${({ theme }): string => theme.colors.primary.variant8};
  margin-top: 0.25rem;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      margin-right: 0.625rem;
      margin-top: 0;
      margin-left: 0.625rem;
      white-space: nowrap;
    }
  `}
`;

export const MessageOptionsWrapStyled = styled(FlexContainer)`
  margin-top: 0.625rem;
`;

export const AssignToWCToggleWrapStyled = styled(FlexContainer)`
  margin-right: 0.375rem;
`;

export const AssignToWCToggleTextStyled = styled(Typography)`
  color: ${({ theme }): string => theme.colors.primary.variant1};
`;
